import React from "react";
import {
  MdOutlineAccountBalanceWallet,
  MdAccountBalanceWallet,
  MdOutlineSportsEsports,
  MdSportsEsports,
  MdOutlineMovie,
  MdMovie,
  MdOutlineShare,
  MdShare,
  MdElderly,
  MdSkateboarding,
  MdOutlineAccessibilityNew,
} from "react-icons/md";
import { Socket } from "socket.io-client";

import FakeWebsite from "../renderTemplates/FakeWebsite";
import FakeDomain from "../renderTemplates/FakeDomain";
import ChatAnswer from "../renderTemplates/ChatAnswer";
import WhoIsRight from "../renderTemplates/WhoIsRight";
import SearchEngineResults from "../renderTemplates/SearchEngineResults";
import SeveralOption from "../renderTemplates/SeveralOption";
import CorrectlyOrderQuestion from "../renderTemplates/CorrectlyOrderQuestion";
import CircleOrCrossOut from "../renderTemplates/CircleOrCrossOut";
import ContentIsScamOrNot from "../renderTemplates/ContentIsScamOrNot";
import MatchStatements from "../renderTemplates/MatchStatements";
import { CorrectOrIncorrect, QuestionId, QuestionKey, RenderTemplate } from "@common";
import { IPlayer } from "../contexts/websocketContext";
import { StatValues } from "../interfaces/answerStats.interface";
import { QuizSlideDataSequence } from "../interfaces/question.interface";
import { TestPasswordStrength } from "@natklok/renderTemplates/TestPasswordStrength";
import ImageIsScamOrNot from "@natklok/renderTemplates/ImageIsScamOrNot";

/*
 *            ██╗██╗
 * ███╗   ██╗ █████╗ ████████╗██╗  ██╗██╗      ██████╗ ██╗  ██╗
 * ████╗  ██║██╔══██╗╚══██╔══╝██║ ██╔╝██║     ██╔═══██╗██║ ██╔╝
 * ██╔██╗ ██║███████║   ██║   █████╔╝ ██║     ██║   ██║█████╔╝
 * ██║╚██╗██║██╔══██║   ██║   ██╔═██╗ ██║     ██║   ██║██╔═██╗
 * ██║ ╚████║██║  ██║   ██║   ██║  ██╗███████╗╚██████╔╝██║  ██╗
 * ╚═╝  ╚═══╝╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═╝  ╚═╝
 *
 * Global configuration for Nätklok
 * --------------------------------
 * Added in v1.3.0
 *
 *
 *
 */

/**
 *
 * Hex colors that are used across the app.
 */
export const pink: Record<number, string> = {
  900: "#240314",
  800: "#54072f",
  700: "#830c49",
  600: "#b31064",
  500: "#e2147e",
  400: "#e84599",
  300: "#ee77b4",
  200: "#f4a8cf",
  100: "#fad9ea",
};
export const babyBlue: Record<number, string> = {
  900: "#051d26",
  800: "#0b4357",
  700: "#116a88",
  600: "#1790ba",
  500: "#1db6eb",
  400: "#4cc5ef",
  300: "#7cd5f3",
  200: "#abe4f8",
  100: "#dbf3fc",
};
export const purple: Record<number, string> = {
  900: "#080818",
  800: "#131238",
  700: "#1d1c57",
  600: "#282777",
  500: "#323196",
  400: "#5d5cac",
  300: "#8888c2",
  200: "#b3b3d8",
  100: "#dedeee",
};
export const gold: Record<number, string> = {
  900: "#251e0c",
  800: "#56441b",
  700: "#876b2a",
  600: "#b8923a",
  500: "#e9b949",
  400: "#eec86f",
  300: "#f2d695",
  200: "#f7e5bc",
  100: "#fbf4e2",
};
export const red: Record<number, string> = {
  900: "#220b0b",
  800: "#4f1a1a",
  700: "#7c2828",
  600: "#a93737",
  500: "#d64545",
  400: "#df6c6c",
  300: "#e79393",
  200: "#f0baba",
  100: "#f8e1e1",
};
export const green: Record<number, string> = {
  900: "#061b15",
  800: "#0e3f30",
  700: "#17634c",
  600: "#1f8767",
  500: "#27ab83",
  400: "#54bd9d",
  300: "#82ceb7",
  200: "#afe0d1",
  100: "#dcf2eb",
};

/**
 *
 * If i18next functionality should be enabled.
 */
export const enableI18N = false;

/**
 *
 * Which "profiles", or activities online, the user
 * can choose at the beginning of the test.
 */
export const profiles = ["gamer", "banker", "movie-watcher", "on-social-media", "general"] as const;

/**
 *
 * Different age groups enables us to target important
 * questions to the right user.
 */
export const ages = ["youth", "adult", "elderly"] as const;

/**
 *
 * Languages that the test currently is translated to.
 */
export const languages = ["sv", "en"] as const;

/**
 *
 * Which badges the test should categorize its questions
 * into. the sv/en values appear in the URL.
 */
export const badges = {
  passwords: {
    sv: "losenord",
    en: "passwords",
  },
  phishing: {
    sv: "phishing",
    en: "phishing",
  },
  theft: {
    sv: "stold-online",
    en: "theft",
  },
  connected: {
    sv: "uppkopplad",
    en: "connected",
  },
  onlineChat: {
    sv: "i-chatten",
    en: "online-chat",
  },
  malware: {
    sv: "skadeprogram",
    en: "malware",
  },
  surfOnline: {
    sv: "surfa-online",
    en: "surf-online",
  },
} as const;
export const badgeIds = Object.keys(badges);
export type Leaderboard = Array<{ score: number; playerIds: Array<string> }>;
export type RoomStatus = "waiting" | "quiz" | "results";

// Recieved by Client
export interface ServerToClientEvents {
  serverMessage: (text: string) => void;
  clientDataUpdated: (newPlayer: IPlayer) => void;
  sendBufferedGameData: (
    state: RoomStatus,
    quizSlideDataSequence: QuizSlideDataSequence,
    currentQuestionIndex: number,
    showReconnectModal: boolean,
    chatAllowed: boolean
  ) => void;
  roomHosted: () => void;
  roomJoined: (players: Array<IPlayer>, code: string) => void;
  roomLeft: (player: IPlayer) => void;
  quizOver: () => void;
  startQuiz: (quizSlideDataSequence: QuizSlideDataSequence) => void;
  nextQuestion: () => void;
  updateAnswerStats: (
    quizSlideDataSequence: QuizSlideDataSequence,
    leaderboard: Leaderboard
  ) => void;
  gameRestarted: () => void;
  gameEnded: () => void;
  sendComment: (text: string, username: string) => void;
  toggleBanPlayer: (playerId: string, isBanned: boolean) => void;
  chatAllowed: (val: boolean) => void;
}

// Recieved by Server
export interface ClientToServerEvents {
  frontendHeartbeat: () => void;
  readyForNextQuestion: (questionIdInSv: string) => void;
  hostRoom: () => void;
  joinRoom: (code: string) => void;
  startGame: () => void;
  leaveRoom: () => void;
  requestUpdateClientData: (inputName: string) => void;
  nextQuestion: () => void;
  quizOver: () => void;
  setQuiz: (quizSlideDataSequence: QuizSlideDataSequence) => void;
  answerQuestion: (
    questionId: QuestionId,
    correctOrIncorrect: CorrectOrIncorrect,
    answerValues: StatValues,
    isQuestion: boolean
  ) => void;
  restartGame: () => void;
  endGame: () => void;
  sendComment: (text: string) => void;
  toggleBanPlayer: (playerId: string, isBanned: boolean) => void;
  setChatAllowed: (val: boolean) => void;
}

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 *
 * All templates used to render questions. These can be
 * dynamic, or simply choice-questions.
 */
const rWrapper = (el: any) => (renderProps: any) => {
  return React.createElement(el, {
    ...renderProps,
  });
};
export const renderTemplateComponents: Record<RenderTemplate, any> = {
  "yes-no": () => null,
  "who-is-right": rWrapper(WhoIsRight),
  "several-option": rWrapper(SeveralOption),
  "test-password-strength": rWrapper(TestPasswordStrength),
  "chat-answer": rWrapper(ChatAnswer),
  "fake-website": rWrapper(FakeWebsite),
  "fake-domain": rWrapper(FakeDomain),
  "search-engine-results": rWrapper(SearchEngineResults),
  "order-correctly": rWrapper(CorrectlyOrderQuestion),
  "circle-or-cross-out": rWrapper(CircleOrCrossOut),
  "content-is-scam-or-not": rWrapper(ContentIsScamOrNot),
  "image-is-scam-or-not": rWrapper(ImageIsScamOrNot),
  "match-statements": rWrapper(MatchStatements),
} as const;
export const renderTemplates = Object.keys(renderTemplateComponents);

/**
 *
 * For statistical purposes, all questions are divided
 * into categories of crime.
 */
export const crimeCategories = [
  "kortbedragerier",
  "identitetsbedragerier",
  "fakturabedragerier",
  "annonsbedragerier",
  "social-manipulation",
  "intrang",
  "natfiske",
  "smishing",
] as const;

/**
 *
 * Which keys the questions-object is filterable by
 */
export const availableFilters: Readonly<Array<QuestionKey>> = [
  "variant",
  "id",
  "fitsAges",
  "fitsProfiles",
  "badge",
] as const;

/**
 *
 * Global settings
 */
export const maxAmountOfQuestionsInTest = 8;
export const webStorageKeyPrefix = "natklok";
export const badgeColors = {
  passwords: ["#E7DA7E", "#ECC345", "#A78408"],
  phishing: ["#E28484", "#ED2727", "#A70808"],
  theft: ["#40CC3D", "#1BBD18", "#1B8218"],
  connected: ["#9E50ED", "#5F00BE", "#420084"],
  onlineChat: ["#DCB6DF", "#5956F0", "#1920C7"],
  malware: ["#B6A400", "#DB9E00", "#BFAD0A"],
  surfOnline: ["#7EC7E7", "#459CEC", "#082BA7"],
};
export const profileStyling = {
  banker: {
    icon: {
      default: React.createElement(MdOutlineAccountBalanceWallet),
      checked: React.createElement(MdAccountBalanceWallet),
    },
    color: "#0EE08C",
    dark: true,
  },
  gamer: {
    icon: {
      default: React.createElement(MdOutlineSportsEsports),
      checked: React.createElement(MdSportsEsports),
    },
    color: "#FDCF35",
    dark: true,
  },
  "movie-watcher": {
    icon: {
      default: React.createElement(MdOutlineShare),
      checked: React.createElement(MdShare),
    },
    color: "#FB733F",
    dark: true,
  },
  "on-social-media": {
    icon: {
      default: React.createElement(MdOutlineMovie),
      checked: React.createElement(MdMovie),
    },
    color: "#FF3854",
    dark: false,
  },
} as const;
export const ageIcons = {
  youth: React.createElement(MdSkateboarding),
  adult: React.createElement(MdOutlineAccessibilityNew),
  elderly: React.createElement(MdElderly),
};

/**
 *
 *
 * Which paths that should have MUI light-mode enabled.
 * NOTE: The rest will be rendered using dark-mode.
 * NOTE: Both palettes can be edited in ./theme.js
 */
export const pathsWithLightMode = ["/multiplayer/skapa", "/multiplayer/joina"];
